import React, { useEffect, useState } from 'react';
import { Plus, Search, Edit3, Trash2, FileText, Save, X, AlertCircle, Book } from 'lucide-react';
import { getCatatan, createCatatan, updateCatatan, deleteCatatan } from '../../api';
import { useAuth } from '../../AuthContext';

export default function ListCatatan() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ judul: '', keterangan1: '', keterangan2: '' });

  const load = () => {
    setLoading(true);
    getCatatan()
      .then(res => {
        setData(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (search) {
      const q = search.toLowerCase();
      setFiltered(data.filter(d =>
        d.judul?.toLowerCase().includes(q) ||
        d.keterangan1?.toLowerCase().includes(q) ||
        d.keterangan2?.toLowerCase().includes(q)
      ));
    } else {
      setFiltered(data);
    }
  }, [search, data]);

  const resetForm = () => {
    setForm({ judul: '', keterangan1: '', keterangan2: '' });
    setEditId(null);
    setError('');
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      judul: item.judul || '',
      keterangan1: item.keterangan1 || '',
      keterangan2: item.keterangan2 || '',
    });
    setEditId(item.id);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.judul.trim()) {
      setError('Judul catatan harus diisi');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await updateCatatan(editId, form);
      } else {
        await createCatatan(form);
      }
      resetForm();
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan catatan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus catatan ini?')) return;
    try {
      await deleteCatatan(id);
      setData(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus');
    }
  };

  const canCreate = hasMenuAction('catatan', 'create');
  const canEdit = hasMenuAction('catatan', 'edit');
  const canDelete = hasMenuAction('catatan', 'delete');

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Book size={24} />
              Catatan
            </h2>
            <p className="page-desc">Mencatat hal-hal yang perlu dicatat ({filtered.length} catatan)</p>
          </div>
          {canCreate && !showForm && (
            <button className="btn btn-primary" onClick={openCreate}
              style={{ background: '#059669', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={16} />
              <span>Catatan Baru</span>
            </button>
          )}
        </div>
      </div>

      {/* Form Create/Edit */}
      {showForm && (
        <div style={{
          background: 'white', borderRadius: 12, padding: 24, marginBottom: 24,
          border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem' }}>
              <FileText size={18} />
              {editId ? 'Edit Catatan' : 'Buat Catatan Baru'}
            </h3>
            <button onClick={() => { resetForm(); setShowForm(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
              <X size={18} />
            </button>
          </div>

          {error && (
            <div className="form-error" style={{ marginBottom: 16 }}>
              <AlertCircle size={14} /><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Judul <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={form.judul}
                  onChange={(e) => setForm({ ...form, judul: e.target.value })}
                  placeholder="Masukkan judul catatan"
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Keterangan 1</label>
                <textarea
                  value={form.keterangan1}
                  onChange={(e) => setForm({ ...form, keterangan1: e.target.value })}
                  placeholder="Masukkan keterangan (opsional)"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                />
              </div>
              <div>
                <label style={labelStyle}>Keterangan 2</label>
                <textarea
                  value={form.keterangan2}
                  onChange={(e) => setForm({ ...form, keterangan2: e.target.value })}
                  placeholder="Masukkan keterangan tambahan (opsional)"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={() => { resetForm(); setShowForm(false); }}>
                Batal
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{
                background: '#059669', color: 'white', border: 'none',
                padding: '10px 24px', borderRadius: 8, fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                opacity: saving ? 0.7 : 1
              }}>
                <Save size={16} />
                {saving ? 'Menyimpan...' : editId ? 'Simpan Perubahan' : 'Simpan Catatan'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative', maxWidth: 400 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Cari catatan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 36px', borderRadius: 8,
              border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat catatan...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px', background: '#f9fafb',
          borderRadius: 16, border: '1px dashed #d1d5db'
        }}>
          <Book size={48} style={{ color: '#9ca3af', marginBottom: 12, opacity: 0.5 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>
            {search ? 'Catatan Tidak Ditemukan' : 'Belum Ada Catatan'}
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            {search ? 'Coba gunakan kata kunci lain.' : 'Mulai dengan membuat catatan baru.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(c => (
            <div key={c.id} style={{
              background: 'white', borderRadius: 10, padding: '16px 20px',
              border: '1px solid #e5e7eb', transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <FileText size={16} style={{ color: '#059669', flexShrink: 0 }} />
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>
                      {c.judul}
                    </h4>
                  </div>
                  {c.keterangan1 && (
                    <p style={{ margin: '4px 0', fontSize: '0.84rem', color: '#4b5563', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {c.keterangan1}
                    </p>
                  )}
                  {c.keterangan2 && (
                    <p style={{ margin: '4px 0', fontSize: '0.84rem', color: '#6b7280', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {c.keterangan2}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.72rem', color: '#9ca3af' }}>
                    <span>Dibuat oleh: {c.creator_name || '-'}</span>
                    <span>Diperbarui: {c.updated_at ? new Date(c.updated_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 16, flexShrink: 0 }}>
                  {canEdit && (
                    <button className="btn btn-sm" title="Edit"
                      onClick={() => openEdit(c)}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
                      <Edit3 size={13} style={{ color: '#6b7280' }} />
                    </button>
                  )}
                  {canDelete && (
                    <button className="btn btn-sm" title="Hapus"
                      onClick={() => handleDelete(c.id)}
                      style={{ padding: '6px 10px', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', background: 'white' }}>
                      <Trash2 size={13} style={{ color: '#ef4444' }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: '0.82rem', fontWeight: 600, color: '#374151',
  marginBottom: 6
};

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: '0.88rem',
  outline: 'none', boxSizing: 'border-box',
  background: 'white', transition: 'border-color 0.15s'
};
